"use client";

import React, { useState, useEffect } from "react";
import { Lock, ShieldCheck, FileText, Activity, CircleAlert, ScanEye, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SHARED_DATA = {
  business_name: 'Адвокат Николай Кондрашов',
  address: 'г. Калуга, ул. Кирова, д. 2',
  phone: '+7 (995) 305-79-51',
  email: 'n.kondrashov@advokat.ru'
};
function SecureEngagementForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const minimalInputClass = "border-0 border-b border-slate-200 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 transition-all duration-300 placeholder:text-slate-400/50 h-12 text-foreground font-sans";
  if (isSubmitted && !submitError) {
    return <div data-section-id="2148" className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="relative">
          <div className="absolute inset-0 bg-primary/10 blur-xl rounded-full animate-pulse" />
          <ShieldCheck className="size-24 text-primary relative z-10" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold tracking-tight text-foreground font-serif">Запрос отправлен</h3>
          <p className="text-muted-foreground max-w-md mx-auto font-sans">{successMessage}</p>
        </div>
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-sm flex items-center gap-3 text-sm text-primary/80 font-mono">
          <Lock className="size-4" />
          <span>ID ЗАПРОСА: {Math.random().toString(36).substr(2, 9).toUpperCase()}</span>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {}
        <FormField control={control} name="name" rules={{
        required: "Пожалуйста, укажите ваше имя"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-widest text-secondary font-mono">ФИО доверителя</FormLabel>
              <FormControl>
                <Input placeholder="ИВАНОВ ИВАН ИВАНОВИЧ" {...field} className={minimalInputClass} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email обязателен",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Некорректный адрес почты"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-widest text-secondary font-mono">Электронная почта</FormLabel>
              <FormControl>
                <Input type="email" placeholder="IVANOV@EXAMPLE.RU" {...field} className={minimalInputClass} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Телефон обязателен"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-widest text-secondary font-mono">Контактный телефон</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+7 (999) 000-00-00" {...field} className={minimalInputClass} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-widest text-secondary font-mono">Организация / Компания</FormLabel>
              <FormControl>
                <Input placeholder="ООО 'ВЕКТОР'" {...field} className={minimalInputClass} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="services" rules={{
      required: "Выберите хотя бы одну услугу"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-widest text-secondary font-mono mb-4 block">Интересующие услуги</FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["Гражданские споры", "Арбитраж", "Семейное право", "Наследственные дела"].map((service, i) => <div key={service} data-index={i} className="flex items-center space-x-3 p-3 border border-slate-100 rounded-sm bg-background/50 hover:bg-slate-50 transition-colors group">
                  <FormControl>
                    <input type="checkbox" className="appearance-none size-4 border border-primary/40 rounded-sm bg-transparent checked:bg-primary checked:border-primary focus:ring-1 focus:ring-primary/50 cursor-pointer relative after:content-[''] after:hidden checked:after:block after:absolute after:left-[5px] after:top-[1px] after:w-[5px] after:h-[10px] after:border-r-2 after:border-b-2 after:border-primary-foreground after:rotate-45 transition-all" checked={field.value?.includes(service)} onChange={e => {
              const checked = e.target.checked;
              const current = field.value || [];
              const updated = checked ? [...current, service] : current.filter((val: string) => val !== service);
              field.onChange(updated);
            }} />
                  </FormControl>
                  <span className="text-xs font-medium text-foreground/80 group-hover:text-primary transition-colors uppercase tracking-wide font-sans">{service}</span>
                </div>)}
            </div>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="details" rules={{
      required: "Пожалуйста, опишите суть вашего вопроса"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-widest text-secondary font-mono">Описание дела</FormLabel>
            <FormControl>
              <Textarea placeholder="Кратко опишите ситуацию, текущий статус дела и желаемый результат." {...field} className="min-h-[120px] bg-background border-slate-200 focus-visible:border-primary resize-none rounded-sm font-sans text-sm" />
            </FormControl>
            <FormDescription className="text-[10px] text-slate-400 font-mono flex items-center gap-1">
              <Lock className="size-3" /> КОНФИДЕНЦИАЛЬНОСТЬ ГАРАНТИРОВАНА
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="preferred_start_date" render={({
      field
    }) => <FormItem className="max-w-xs">
            <FormLabel className="text-[10px] uppercase tracking-widest text-secondary font-mono">Желаемая дата консультации</FormLabel>
            <FormControl>
              <Input type="date" {...field} className={minimalInputClass} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-sm flex items-start gap-3 animate-in slide-in-from-top-2">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-destructive font-mono uppercase">Ошибка отправки</p>
            <p className="text-xs text-destructive/80 font-sans">{submitError}</p>
          </div>
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm font-bold tracking-[0.2em] uppercase bg-primary text-primary-foreground hover:bg-primary/95 shadow-lg border border-primary/10 rounded-sm transition-all hover:scale-[1.01] active:scale-[0.99]">
        {isSubmitting ? <>
            <Activity className="animate-spin mr-2 size-5" />
            ОБРАБОТКА ДАННЫХ...
          </> : <>
            <Lock className="mr-2 size-5" />
            ОТПРАВИТЬ ЗАПРОС
          </>}
      </Button>
    </Form>;
}
export default function SecureEngagement() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="свяжитесь-со-мной" className="bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#47556908_1px,transparent_1px),linear-gradient(to_bottom,#47556908_1px,transparent_1px)] bg-[size:60px_60px]" />
        
        {}
        <div className="absolute -bottom-48 -right-48 size-[600px] rounded-full border border-primary/5 animate-[ping_8s_ease-in-out_infinite] opacity-10" />
        
        {}
        <div className="absolute top-0 left-1/4 w-1/2 h-1/2 bg-slate-200/20 blur-[120px] rounded-full pointer-events-none" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          
          {}
          <div ref={ref as React.RefObject<HTMLDivElement>} className={`lg:col-span-5 space-y-10 pt-8 ${isVisible ? 'animate-in slide-in-from-left-10 fade-in duration-700' : 'opacity-0'}`}>
            <div className="space-y-6">
              <Badge variant="outline" className="border-primary/20 text-primary bg-primary/5 px-3 py-1.5 rounded-full uppercase tracking-widest text-[10px] font-mono">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                Линия связи открыта
              </Badge>
              
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight font-serif leading-tight">
                Свяжитесь <span className="text-primary">со мной</span>
              </h2>
              
              <p className="text-secondary text-lg leading-relaxed border-l-2 border-slate-200 pl-6 font-sans">
                Направьте конфиденциальный запрос для получения юридической помощи по гражданским или арбитражным делам. 
                Ваши данные защищены адвокатской тайной и современными протоколами безопасности.
              </p>
            </div>

            <div className="space-y-4">
              {[{
              icon: ShieldCheck,
              title: "Адвокатская тайна",
              desc: "Полная конфиденциальность всех предоставленных сведений гарантирована законом."
            }, {
              icon: ScanEye,
              title: "Личный подход",
              desc: "Каждое обращение изучается лично адвокатом без участия посредников."
            }, {
              icon: Activity,
              title: "Профессиональный анализ",
              desc: "Оперативная оценка перспектив дела и первичные рекомендации по стратегии защиты."
            }].map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 p-4 rounded-sm border border-slate-100 bg-white/50 backdrop-blur-sm hover:bg-white/80 transition-colors group shadow-sm">
                  <div className="p-2 bg-primary/5 rounded-sm text-primary shrink-0 group-hover:text-primary-foreground group-hover:bg-primary transition-colors">
                    <item.icon className="size-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground font-mono text-sm uppercase tracking-wide">{item.title}</h4>
                    <p className="text-sm text-secondary mt-1 font-sans">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-8 border-t border-slate-100">
              <p className="text-[10px] text-secondary font-mono uppercase tracking-widest mb-4">
                Прямые контакты
              </p>
              <div className="flex flex-col sm:flex-row gap-6 text-sm font-medium font-mono">
                <span className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors cursor-default">
                  <span className="size-1.5 rounded-full bg-primary" />
                  {SHARED_DATA.phone}
                </span>
                <span className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors cursor-default"><span className="size-1.5 rounded-full bg-primary" data-ast-id="f:/src/pages/Contact/sections/SecureEngagement.tsx|sig:1lim6w" data-ast-start="14357" data-ast-end="14410"></span>lawhelpklg@gmail.com</span>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 ${isVisible ? 'animate-in slide-in-from-right-10 fade-in duration-700 delay-200' : 'opacity-0'}`}>
            <Card className="bg-white border-slate-200 shadow-2xl relative overflow-hidden rounded-sm">
              {}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent opacity-70" />
              <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                <FileText className="size-32 -rotate-12" />
              </div>

              <CardHeader className="border-b border-slate-100 pb-6 relative z-10 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-mono uppercase tracking-widest flex items-center gap-2 text-primary">
                      <Lock className="size-4" />
                      Новое обращение
                    </CardTitle>
                    <CardDescription className="mt-1 font-mono text-[10px] text-secondary">
                      РЕЕСТРОВЫЙ № 40/806 | {new Date().getFullYear()}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="hidden sm:flex border-slate-200 text-secondary bg-white font-mono text-[10px] tracking-widest">
                    КОНФИДЕНЦИАЛЬНО
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="pt-8 relative z-10">
                <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="secure-engagement" defaultValues={{
                name: "",
                email: "",
                phone: "",
                organization: "",
                services: [],
                details: "",
                preferred_start_date: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                services: {
                  required: true
                },
                details: {
                  required: true
                }
              }} submitText="Отправить запрос" successMessage="Ваш запрос успешно зашифрован и передан в приемную адвоката. Мы свяжемся с вами в ближайшее время для уточнения деталей.">
                  <SecureEngagementForm />
                </FormProvider>
              </CardContent>
              
              <CardFooter className="bg-slate-50/80 border-t border-slate-100 py-3 flex justify-between items-center text-[10px] text-secondary font-mono uppercase tracking-wider">
                <span>ЗАЩИЩЕННОЕ СОЕДИНЕНИЕ</span>
                <span className="flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-emerald-600 animate-pulse shadow-[0_0_8px_rgba(5,150,105,0.4)]" />
                  СИСТЕМА АКТИВНА
                </span>
              </CardFooter>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}